import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {NavigationExtras, Router} from '@angular/router';
import {TrackerOrderStatus} from '../../../model';
import {camelCaseToDashCase, MIN_START_DATE} from '../../../utils';
import {AnalyticsService} from '../../shared/analytics.service';
import {AppStateService} from '../../shared/app-state.service';
import {AuthService} from '../../shared/auth.service';
import {FirstNamePipe} from '../../shared/first-name.pipe';
import {NameFormatPipe} from '../../shared/name-format.pipe';
import {getActivityType, getPositionStatus, isEndDateUpdatePending, PositionStatus} from '../position-search.pipe';
import {TrackerActivityPipe} from '../tracker-activity.pipe';
import {environment} from '../../../environments/environment';
import {UpdateAssignmentModalComponent} from '../update-assignment-modal/update-assignment-modal.component';
import {BehaviorSubject} from 'rxjs';
import capitalize from 'lodash-es/capitalize';
import * as startOfToday from 'date-fns/start_of_today';
import * as isAfter from 'date-fns/is_after';

@Component({
  selector: 'position-summary',
  templateUrl: './position-summary.component.html',
  styleUrls: ['../styles/position-summary.scss', './position-summary.component.scss']
})
export class PositionSummaryComponent implements OnChanges {

  @ViewChild('updateAssignmentModal') updateAssignmentModal: UpdateAssignmentModalComponent;

  @Input() order: Order;
  @Input() staff: StaffInfo[];
  @Input() hired = false;
  @Input() activityTextProjected = false;
  @Input() returnTo = ['positions'];

  @Output() reload = new EventEmitter<void>();
  @Output() unauthenticatedClick = new EventEmitter<{commands: any[], extras: NavigationExtras}>();

  endOfAssignment = environment.flags.endOfAssignment;

  status: PositionStatus;
  trackerStatusProperty: string;
  agentFirst: string;
  activityType: string;
  activityContent: string | null = null;
  minStartDate = MIN_START_DATE;
  maxEndDate = startOfToday();
  brand$ = this.AppState.brand$;
  user$ = this.Auth.user$;
  assignmentUpdatePending$ = new BehaviorSubject(false);
  tooltipOpen$ = new BehaviorSubject(false);
  isFutureEndDate$ = new BehaviorSubject(true);

  PositionStatus = PositionStatus;

  constructor(private Router: Router,
              private Analytics: AnalyticsService,
              private ActivityPipe: TrackerActivityPipe,
              private Auth: AuthService,
              private AppState: AppStateService) {
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['order'] || changes['hired']) {
      this.status = this.hired ? PositionStatus.Working : getPositionStatus(this.order);
      const trackerStatus = TrackerOrderStatus[this.order.maxTrackerStatus];
      this.trackerStatusProperty = `positions.tracker-status.${camelCaseToDashCase(trackerStatus)}`;
      this.assignmentUpdatePending$.next(isEndDateUpdatePending(this.order));
      this.isFutureEndDate$.next(this.order.endDate && isAfter(this.order.endDate, startOfToday()));
    }
    if (changes['staff']) {
      const agent = this.staff && this.staff.find(v => v.agent);
      if (agent) {
        const locale = this.AppState.locale$.getValue();
        this.agentFirst = FirstNamePipe.apply(NameFormatPipe.apply(agent, locale), locale);
      }
    }

    this.activityContent = this.ActivityPipe.transform(this.order.latestTrackerActivity);
    this.activityType = this.calculateActivityType();
  }

  summaryClicked() {
    if (this.status !== PositionStatus.Completed) {
      if (this.Auth.isFullyAuthenticated() && this.isNotOrderRequest()) {
        this.goToOrder();
      } else {
        this.unauthenticatedClick.emit(this.getRouteForOrder());
      }
    }
  }

  isNotOrderRequest() {
    return !this.order.isRequest;
  }

  goToOrder() {
    this.Analytics.trackEvent('Positions: ' + capitalize(this.status) + ' position detail clicked');

    const route = this.getRouteForOrder();
    this.Router.navigate(route.commands, route.extras);
  }

  getRouteForOrder(): {commands: any[], extras: NavigationExtras} {
    if (this.hired) {
      return {
        commands: ['/positions', this.order.orderId, 'details'],
        extras: {queryParams: {returnTo: [...this.returnTo, 'candidates'].join(',')}}
      };
    }

    return {
      commands: ['/positions', this.order.orderId, this.status === PositionStatus.Open ? 'candidates' : 'details'],
      extras: {queryParams: {returnTo: [...this.returnTo].join(',')}}
    };
  }

  titleForStatus(status: TrackerOrderStatus) {
    if (status <= TrackerOrderStatus.hasCandidates) {
      return 'positions.tracker-status.has-candidates';
    } else if (status === TrackerOrderStatus.hasSubmittals) {
      return 'positions.tracker-status.has-submittals';
    } else if (status === TrackerOrderStatus.interviewing) {
      return 'positions.tracker-status.interviewing';
    } else if (status === TrackerOrderStatus.offerExtended) {
      return 'positions.tracker-status.offer-extended';
    }

    return 'positions.tracker-status.offer-accepted';
  }

  iconForStatus(status: TrackerOrderStatus, brand: string) {
    const vt = brand === 'vt' ? '-VT' : '';

    if (status <= TrackerOrderStatus.hasCandidates) {
      return `static/position/requested${vt}.svg`;
    } else if (status === TrackerOrderStatus.hasSubmittals) {
      return `static/position/reviewing${vt}.svg`;
    } else if (status === TrackerOrderStatus.interviewing) {
      return `static/position/interviewing${vt}.svg`;
    } else if (status === TrackerOrderStatus.offerExtended) {
      return `static/position/offer-extended${vt}.svg`;
    }

    return `static/position/hired${vt}.svg`;
  }

  private calculateActivityType() {
    const activityType = getActivityType(this.status, this.order, this.activityContent);

    return (!activityType && this.activityTextProjected) ? 'activity_text' : activityType;
  }

  editEndDate(event: Event) {
    event.stopPropagation();
    this.updateAssignmentModal.show('proactive');
  }

  toggleUpdatePendingTooltip(event: Event) {
    event.stopPropagation();
    this.tooltipOpen$.next(!this.tooltipOpen$.getValue());
  }

  onAssignmentUpdated() {
    this.reload.emit();
    this.Analytics.trackEvent('Positions: Assignment proactively updated');
  }
}
